import * as React from "react";
import styled, { css } from "styled-components";
import { select } from "@hubper/theme";
import mergeHandlers from "../../util/mergeHandlers";
import composeRefs from "../../util/composeRefs";
import InputContainer, { InputContainerProps } from "../InputContainer";

const Base = styled(InputContainer)`
  height: auto;
  padding: ${select.space(3)};
`;

const phBase = css`
  color: ${select.color(["grey", 3])};
`;

export const inputPlaceholderStyle = css`
  ::-webkit-input-placeholder {
    /* Chrome/Opera/Safari */
    ${phBase}
  }
  ::-moz-placeholder {
    /* Firefox 19+ */
    ${phBase}
  }
  :-ms-input-placeholder {
    /* IE 10+ */
    ${phBase}
  }
  :-moz-placeholder {
    /* Firefox 18- */
    ${phBase}
  }
`;

export const inputResetStyle = css`
  appearance: none;
  box-sizing: border-box;
  box-shadow: none;
  font-family: inherit;
  font-size: ${select.fontSize(-1)};
  color: inherit;
  outline: 0;
  background-color: inherit;
  border: 0;
  ${inputPlaceholderStyle}
`;

const PlainTextarea = styled.textarea`
  ${inputResetStyle}
  height: 100%;
  width: 100%;
  resize: none;
`;

type Props = {
  containerRef?: any;
  value?: string;
} & React.HTMLAttributes<HTMLTextAreaElement> &
  InputContainerProps;

function TextArea(
  {
    containerRef,
    className,
    disabled,
    error,
    label,
    focus,
    style,
    stretch,
    onClick,
    ...rest
  }: Props,
  ref: any
) {
  const [hasFocus, setFocus] = React.useState(false);
  const inputRef = React.useRef<HTMLTextAreaElement>(null);

  return (
    <Base
      className={className}
      ref={containerRef}
      focus={hasFocus}
      disabled={disabled}
      error={error}
      stretch={stretch}
      label={label}
      style={style}
      onClick={(evt: any) => {
        if (evt.target !== inputRef.current) {
          inputRef.current!.focus();
        }
      }}
    >
      <PlainTextarea
        {...rest}
        disabled={disabled}
        ref={composeRefs(ref, inputRef)}
        onFocus={mergeHandlers(rest.onFocus, () => {
          setFocus(true);
        })}
        onBlur={mergeHandlers(rest.onBlur, () => {
          setFocus(false);
        })}
      />
    </Base>
  );
}

export default React.forwardRef(TextArea);
